import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Building2, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { optimizedImageUrl } from '@/lib/imageOptimizer';

const PAGE_SIZE = 8; // 4 cols x 2 rows on desktop
const AUTO_MS = 5000;

type AgencyCard = {
  id: string;
  name: string;
  logo_url: string | null;
  city: string | null;
};

export function TitaniumAgencies() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);

  const { data: agencies } = useQuery<AgencyCard[]>({
    queryKey: ['titanium-agencies-compact'],
    queryFn: async () => {
      const { data: ags } = await supabase
        .from('agencies')
        .select('id, name, logo_url, city')
        .eq('is_verified', true)
        .order('created_at', { ascending: false })
        .limit(40);
      return (ags || []) as AgencyCard[];
    },
  });

  const pages = useMemo(() => {
    if (!agencies?.length) return [] as AgencyCard[][];
    const out: AgencyCard[][] = [];
    for (let i = 0; i < agencies.length; i += PAGE_SIZE) out.push(agencies.slice(i, i + PAGE_SIZE));
    return out;
  }, [agencies]);

  // --- Drag / momentum state ---
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0); // px during drag
  const dragState = useRef({
    active: false,
    startX: 0,
    lastX: 0,
    lastT: 0,
    velocity: 0, // px / ms
    width: 0,
    pointerId: 0 as number,
    moved: false,
  });

  useEffect(() => {
    if (paused || dragging || pages.length <= 1) return;
    const t = setInterval(() => setPage(p => (p + 1) % pages.length), AUTO_MS);
    return () => clearInterval(t);
  }, [paused, dragging, pages.length]);

  if (!agencies?.length) return null;

  const clamp = (p: number) => Math.max(0, Math.min(pages.length - 1, p));
  const prev = () => setPage(p => (p - 1 + pages.length) % pages.length);
  const next = () => setPage(p => (p + 1) % pages.length);

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const w = viewportRef.current?.clientWidth || 0;
    dragState.current = {
      active: true,
      startX: e.clientX,
      lastX: e.clientX,
      lastT: performance.now(),
      velocity: 0,
      width: w,
      pointerId: e.pointerId,
      moved: false,
    };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setDragging(true);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const s = dragState.current;
    if (!s.active) return;
    const now = performance.now();
    const dx = e.clientX - s.lastX;
    const dt = Math.max(1, now - s.lastT);
    // EMA velocity for smoother momentum estimate
    s.velocity = s.velocity * 0.7 + (dx / dt) * 0.3;
    s.lastX = e.clientX;
    s.lastT = now;
    const total = e.clientX - s.startX;
    if (Math.abs(total) > 4) s.moved = true;

    // Rubber-band at edges
    let offset = total;
    if (page === 0 && offset > 0) offset = offset * 0.35;
    if (page === pages.length - 1 && offset < 0) offset = offset * 0.35;
    setDragOffset(offset);
  };

  const endDrag = (e: React.PointerEvent) => {
    const s = dragState.current;
    if (!s.active) return;
    s.active = false;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(s.pointerId); } catch {}

    const w = s.width || 1;
    const distanceRatio = dragOffset / w; // -1..1 typical
    const velocityPx = s.velocity * 1000; // px / s
    // Project final offset with momentum (decay factor)
    const projected = dragOffset + velocityPx * 0.18;
    const projectedRatio = projected / w;

    let target = page;
    if (projectedRatio > 0.18) target = page - 1;
    else if (projectedRatio < -0.18) target = page + 1;
    else if (distanceRatio > 0.5) target = page - 1;
    else if (distanceRatio < -0.5) target = page + 1;

    setPage(clamp(target));
    setDragOffset(0);
    setDragging(false);

    // If user actually dragged, swallow the click on child buttons
    if (s.moved) {
      const stop = (ev: Event) => { ev.stopPropagation(); ev.preventDefault(); };
      window.addEventListener('click', stop, { capture: true, once: true });
    }
  };

  const translatePct = -(page * 100);
  const dragPx = dragOffset;

  return (
    <section className="py-6 md:py-8">
      <div className="container">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg md:text-xl font-bold">Titanium Agencies</h2>
          <button
            onClick={() => navigate('/agencies')}
            className="text-xs md:text-sm font-semibold text-primary hover:underline"
          >
            View all
          </button>
        </div>

        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {pages.length > 1 && (
            <>
              <button
                onClick={prev}
                aria-label="Previous"
                className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-background border shadow-sm items-center justify-center hover:bg-muted"
              >
                <ChevronLeft className="h-4 w-4 text-primary" />
              </button>
              <button
                onClick={next}
                aria-label="Next"
                className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-background border shadow-sm items-center justify-center hover:bg-muted"
              >
                <ChevronRight className="h-4 w-4 text-primary" />
              </button>
            </>
          )}

          <div
            ref={viewportRef}
            className="overflow-hidden touch-pan-y select-none"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            style={{ cursor: dragging ? 'grabbing' : 'grab' }}
          >
            <div
              ref={trackRef}
              className="flex"
              style={{
                transform: `translate3d(calc(${translatePct}% + ${dragPx}px), 0, 0)`,
                transition: dragging
                  ? 'none'
                  : 'transform 600ms cubic-bezier(0.22, 1, 0.36, 1)',
                willChange: 'transform',
              }}
            >
              {pages.map((group, gi) => (
                <div
                  key={gi}
                  className="w-full shrink-0 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 grid-rows-2 gap-3"
                >
                  {group.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => navigate(`/agencies/${a.id}`)}
                      draggable={false}
                      className="flex items-center gap-3 p-2.5 rounded-lg border bg-card hover:shadow-sm hover:border-primary/40 transition-all text-left"
                    >
                      <div className="h-14 w-14 shrink-0 rounded-md border bg-background flex items-center justify-center overflow-hidden">
                        {a.logo_url ? (
                          <img
                            src={optimizedImageUrl(a.logo_url, { width: 112, height: 112 })}
                            alt={a.name}
                            loading="lazy"
                            draggable={false}
                            className="h-full w-full object-contain pointer-events-none"
                          />
                        ) : (
                          <Building2 className="h-6 w-6 text-muted-foreground" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-bold text-sm leading-tight truncate">{a.name}</h3>
                        <div className="flex items-center gap-1 text-xs text-emerald-600 mt-1">
                          <MapPin className="h-3 w-3 shrink-0" />
                          <span className="truncate">{a.city || 'Ethiopia'}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
